import React from 'react';
import styles from './Gauge.module.css';

interface GaugeProps {
  score: number | null;
  /** Optional letter grade (A/B/C/D/F). If omitted, derived from score. */
  grade?: string | null;
  /** Rendered pixel size; the SVG scales responsively up to this width. */
  size?: number;
  /** Stroke width of the ring. Defaults to 5 for sizes <120, 8 otherwise. */
  strokeWidth?: number;
  /** Where to render the grade label.
   *  - 'badge' (default): pill below the ring
   *  - 'inline': inside the ring under the score
   *  - 'none': omit
   */
  showGrade?: 'badge' | 'inline' | 'none';
}

function scoreToGrade(s: number): string {
  return s >= 85 ? 'A' : s >= 70 ? 'B' : s >= 55 ? 'C' : s >= 40 ? 'D' : 'F';
}

function scoreColor(s: number): string {
  return s >= 75 ? 'var(--success)' : s >= 55 ? 'var(--warning)' : 'var(--error)';
}

function scoreBg(s: number): string {
  return s >= 75 ? 'var(--success-bg)' : s >= 55 ? 'var(--warning-bg)' : 'var(--error-bg)';
}

export function Gauge({
  score,
  grade: gradeProp,
  size = 96,
  strokeWidth,
  showGrade = 'badge',
}: GaugeProps) {
  const stroke = strokeWidth ?? (size >= 120 ? 8 : 5);
  const r = (size - stroke * 2 - 2) / 2;
  const c = Math.PI * 2 * r;

  if (score == null) {
    return (
      <div className={styles.root}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className={styles.svg}
          style={{ maxWidth: size }}
          aria-hidden
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--border)"
            strokeWidth={stroke}
          />
          <text
            x={size / 2}
            y={size / 2 - 4}
            textAnchor="middle"
            dominantBaseline="central"
            fill="var(--text-muted)"
            fontSize="22"
            fontWeight="700"
          >
            —
          </text>
          <text
            x={size / 2}
            y={size / 2 + 14}
            textAnchor="middle"
            dominantBaseline="central"
            fill="var(--text-muted)"
            fontSize="10"
          >
            No data
          </text>
        </svg>
      </div>
    );
  }

  const pct = Math.min(Math.max(score, 0) / 100, 1);
  const grade = gradeProp ?? scoreToGrade(score);
  const color = scoreColor(score);
  const bg = scoreBg(score);

  return (
    <div className={styles.root}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className={styles.svg}
        style={{ maxWidth: size }}
        aria-hidden
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--border)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${c * pct} ${c * (1 - pct)}`}
          strokeDashoffset={c * 0.25}
          strokeLinecap="round"
          className={styles.progressArc}
        />
        <text
          x={size / 2}
          y={size / 2 - (showGrade === 'inline' ? 10 : 6)}
          textAnchor="middle"
          dominantBaseline="central"
          fill={color}
          fontSize="22"
          fontWeight="700"
          fontFamily="var(--font-primary)"
        >
          {score}
        </text>
        <text
          x={size / 2}
          y={size / 2 + (showGrade === 'inline' ? 8 : 14)}
          textAnchor="middle"
          dominantBaseline="central"
          fill="var(--text-muted)"
          fontSize="9"
          fontFamily="var(--font-primary)"
        >
          / 100
        </text>
        {showGrade === 'inline' && (
          <text
            x={size / 2}
            y={size / 2 + 24}
            textAnchor="middle"
            dominantBaseline="central"
            fill={color}
            fontSize="11"
            fontWeight="600"
            fontFamily="var(--font-primary)"
          >
            Grade {grade}
          </text>
        )}
      </svg>
      {showGrade === 'badge' && (
        <div className={styles.badge} style={{ color, background: bg }}>
          Grade {grade}
        </div>
      )}
    </div>
  );
}
